import { useEffect, useState, useRef } from "react";
import _ from "lodash";
import uiSetting from "@/apis/uiSetting";
import { getGroupColumnData } from "@/cleanup/utils/tableUtils";

interface MapSettings {
  selectedLayers: unknown[];
  mapLayer: string;
  geozoneLayer: boolean;
  networkLayer: boolean;
  assetLayer: boolean;
  infraLayer: boolean;
  siteMapLayer: boolean;
  layers: unknown[];
}

export interface UiSetting {
  useColumnFilter?: boolean;
  groups?: {
    aggregateColumn: string;
    aggregationType: string;
    bucketType: string;
    bucketConfig: unknown;
  }[];
  orderData?: [string, string] | null;
  columnVisibleInOrder?: string[];
  pageSize?: number;
  pageIndex?: number;
  mapSettings?: MapSettings;
  pageName?: string;
}

interface FixedSetting {
  groups?: {
    aggregateColumn: string;
    aggregationType: string;
    bucketType: string;
    bucketConfig: unknown;
  }[];
  orderData?: [string, string] | null;
  columnVisibleInOrder?: string[];
  pageSize?: number;
  pageIndex?: number;
}

interface FilterColumn {
  immovable?: boolean;
  columnKey: string;
}

interface Filter {
  columns: FilterColumn[];
  sortBy: {
    column: {
      sortKey: string;
    };
    desc: boolean;
  };
  pageSize: number;
  pageIndex: number;
}

export const useUiSetting = (pageName?: string, fixedSetting?: FixedSetting) => {
  const defaultMapSettings = {
    selectedLayers: [],
    mapLayer: "",
    geozoneLayer: false,
    networkLayer: false,
    assetLayer: false,
    infraLayer: false,
    siteMapLayer: true,
    layers: [],
  };
  const [setting, setSetting] = useState<UiSetting>({});
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    if (!pageName) {
      setSetting({
        useColumnFilter: true,
        groups: fixedSetting ? fixedSetting.groups : [],
        orderData: fixedSetting ? fixedSetting.orderData : null,
        columnVisibleInOrder: fixedSetting
          ? fixedSetting.columnVisibleInOrder
          : [],
        pageSize: fixedSetting ? fixedSetting.pageSize : 25,
        pageIndex: fixedSetting ? fixedSetting.pageIndex : 0,
        mapSettings: defaultMapSettings,
      });
      return;
    }

    uiSetting
      .findOne(pageName)
      .then(({ data }) => {
        if (!isMountedRef.current) return;
        if (data.mapSettings) {
          data.mapSettings = JSON.parse(data.mapSettings);
        }

        // If fixedSetting is provided, use it instead of loaded data (override mode)
        if (fixedSetting) {
          setSetting({
            useColumnFilter: true,
            ...fixedSetting,
            mapSettings: data.mapSettings || defaultMapSettings,
          });
        } else {
          setSetting(data);
        }
      })
      .catch((err) => {
        if (!isMountedRef.current) return;
        // in useColumnFilter using setting is not empty
        const fallbackSetting: UiSetting = {
          useColumnFilter: true,
          groups: fixedSetting ? fixedSetting.groups : [],
          orderData: fixedSetting ? fixedSetting.orderData : null,
          columnVisibleInOrder: fixedSetting
            ? fixedSetting.columnVisibleInOrder
            : [],
          pageSize: fixedSetting ? fixedSetting.pageSize : 25,
          pageIndex: fixedSetting ? fixedSetting.pageIndex : 0,
          mapSettings: pageName === "DASHBOARD_MAP" ? {} as MapSettings : defaultMapSettings,
        };
        setSetting(fallbackSetting);
      });

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateSetting = (filter: Filter) => {
    if (!pageName) return;

    const { groups } = getGroupColumnData(filter.columns);
    const nextSetting: UiSetting = {
      columnVisibleInOrder: filter.columns
        .filter((c) => !c.immovable)
        .map((c) => c.columnKey),
      orderData: [
        filter.sortBy.column.sortKey,
        filter.sortBy.desc ? "desc" : "asc",
      ],
      pageSize: filter.pageSize,
      pageIndex: filter.pageIndex,
      pageName: pageName,
      groups: groups.map((item) => {
        return {
          aggregateColumn: item.columnGroupKey, // uiSetting store aggregateColumn = columnKey
          aggregationType: item.aggregationType,
          bucketType: item.bucketType,
          bucketConfig: item.bucketConfig,
        };
      }),
    };
    if (
      !_.isEqual(
        nextSetting.columnVisibleInOrder,
        setting.columnVisibleInOrder
      ) ||
      !_.isEqual(nextSetting.orderData, setting.orderData) ||
      !_.isEqual(nextSetting.pageSize, setting.pageSize) ||
      !_.isEqual(nextSetting.pageIndex, setting.pageIndex) ||
      !_.isEqual(nextSetting.groups, setting.groups)
    ) {
      setSetting(nextSetting);
      uiSetting.updateSetting(nextSetting).then(
        () => {},
        (err) => {}
      );
    }
  };

  const updateDashboardMapSetting = (data: MapSettings) => {
    if (!pageName || !data) return;

    const nextSetting: UiSetting = {
      pageName: pageName,
      mapSettings: { ...data },
    };
    setSetting(nextSetting);
    if (!_.isEqual(data, setting.mapSettings)) {
      uiSetting
        .updateSetting({
          ...nextSetting,
          mapSettings: JSON.stringify(nextSetting.mapSettings),
        })
        .then(
          () => {},
          (err) => {}
        );
    }
  };

  const updateMapSetting = (data: Partial<MapSettings>) => {
    if (!pageName || !data) return;

    const currentMapSettings = setting.mapSettings || ({} as MapSettings);
    const nextSetting: UiSetting = {
      pageName: pageName,
      mapSettings: { ...currentMapSettings, ...data } as MapSettings,
    };
    setSetting(nextSetting);

    if (
      !_.isEqual(data.selectedLayers, currentMapSettings.selectedLayers) ||
      !_.isEqual(data.mapLayer, currentMapSettings.mapLayer) ||
      !_.isEqual(data.geozoneLayer, currentMapSettings.geozoneLayer) ||
      !_.isEqual(data.networkLayer, currentMapSettings.networkLayer) ||
      !_.isEqual(data.assetLayer, currentMapSettings.assetLayer) ||
      !_.isEqual(data.infraLayer, currentMapSettings.infraLayer) ||
      !_.isEqual(data.siteMapLayer, currentMapSettings.siteMapLayer) ||
      !_.isEqual(data.layers, currentMapSettings.layers)
    ) {
      uiSetting
        .updateSetting({
          ...nextSetting,
          mapSettings: JSON.stringify(nextSetting.mapSettings),
        })
        .then(
          () => {},
          (err) => {}
        );
    }
  };

  return {
    setting,
    updateSetting,
    updateMapSetting,
    updateDashboardMapSetting,
  };
};
